import React from 'react'
import styled from '@emotion/styled'
import { useTheme } from 'emotion-theming'
import { bool, number, object, oneOfType, string } from 'prop-types'
import dayjs from 'dayjs'
import { AuthorInfo } from '../AuthorInfo'
import { truncateString } from '@jelly/utils'
import { Link } from '@jelly/ui'

const MAX_BLURB_CHARS = 200

const StyledTitle = styled.div`
	box-sizing: content-box;
	color: ${({ theme }) => theme.colors.text};
	font-size: inherit;
	font-weight: 400;
	margin: 0;
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		font-size: 20px;
	}

	&:hover {
		${StyledTitle} {
			color: ${({ theme }) => theme.hubTemplate.articleTitleHover};
		}
	}
`
const StyledArticle = styled.div`
	${({ inTrendingArticles }) => !inTrendingArticles && `border-bottom: 1px solid rgba(0, 0, 0, 0.1);`}
	align-items: flex-start;
	display: flex;
	padding: 1rem 0;
	word-break: break-word;
	box-sizing: border-box;
`

const ContentWrapper = styled.div`
	align-items: flex-start;
	box-sizing: content-box;
	display: flex;
	flex-direction: column;
	font-size: inherit;
	margin: 0 0 0 1rem;
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		margin: 0 1rem;
	}
`
const Thumbnail = styled.img`
	display: none;
	margin-top: 5px;
	width: 100%;

	@media screen and (min-width: 321px) and (max-width: 375px) {
		display: block;
		max-width: 68px;
	}

	@media screen and (min-width: 376px) {
		display: block;
		max-width: 100px;
	}
`
const StyledBlurb = styled.p`
	color: ${({ theme }) => theme.colors.text};
	font-size: 0.875rem;
	margin: 0;
	line-height: 1.5em;
	padding-top: 3px;
	width: 100%;
`

const StyledSubtitle = styled.div`
	${({ inTrendingArticles }) =>
		inTrendingArticles
			? `
			box-sizing: border-box;
			display: -webkit-box;
			overflow: hidden;
			margin: 0 0 10px 0;
			text-overflow: ellipsis;
			text-transform: none;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
			`
			: `
			margin: 0;
			`}
	color: ${({ theme }) => theme.colors.darkGrey};
	font-size: 0.875rem;
	line-height: normal;
	padding-top: 3px;
	width: 100%;
`
const ArticleTypeBadge = styled.div`
	${({ backgroundColor }) => `background-color: ${backgroundColor}`};
	border-radius: 2px;
	color: ${({ theme }) => theme.colors.white};
	display: flex;
	font-size: 12px;
	justify-content: center;
	margin: 0 10px 0 0;
	padding: 3px 10px;
`
const SponsoredBadge = styled.div`
	font-size: 12px;
	color: ${({ theme }) => theme.colors.gray[500]};
`
const BadgeSection = styled.div`
	align-self: start;
	display: flex;
	margin-bottom: 5px;
`

CardForContentSummary.propTypes = {
	altText: string,
	author: oneOfType([object, string]),
	blurb: string,
	displayModified: string,
	docType: oneOfType([bool, string]),
	inTrendingArticles: bool,
	outerUrl: string,
	subtitle: string,
	sponsor: object,
	isSponsored: bool,
	thumbnail: string,
	title: string.isRequired,
	total_answers: number,
}

export function CardForContentSummary({
	altText = '',
	author,
	blurb,
	displayModified,
	docType,
	inTrendingArticles,
	outerUrl,
	sponsor,
	subtitle,
	isSponsored,
	thumbnail,
	title,
	total_answers,
}) {
	const { badges } = useTheme()
	const ARTICLE_BADGE = {
		article: { text: 'Article', color: badges.article },
		category: { text: 'Category', color: badges.category },
		editorsPick: { text: "Editor's Pick", color: badges.editorsPick },
		question: { text: 'Advice', color: badges.question },
		review: { text: 'Review', color: badges.review },
	}
	// TODO remove this line once everything is coming from Elasticsearch
	const authorString = typeof author === 'string' ? author : author !== null ? author?.displayName : 'Anonymous User'
	const adviceText = total_answers
		? `${total_answers} Answer${total_answers > 1 ? 's' : ''}  | Last Activity on `
		: ''

	let lastWordReview = title.split(' ').pop() === ('review' || 'Review')
	title = docType === 'review' && !lastWordReview ? `${title} Review` : title

	return (
		<>
			<StyledArticle inTrendingArticles={inTrendingArticles}>
				{thumbnail && (
					<Link href={outerUrl}>
						<Thumbnail altText={altText} src={thumbnail} />{' '}
					</Link>
				)}
				<ContentWrapper>
					<BadgeSection>
						{docType && ARTICLE_BADGE[docType] && (
							<ArticleTypeBadge backgroundColor={ARTICLE_BADGE[docType].color}>
								{ARTICLE_BADGE[docType].text}
							</ArticleTypeBadge>
						)}
						{!inTrendingArticles && isSponsored && <SponsoredBadge>Sponsored Content</SponsoredBadge>}
					</BadgeSection>
					<Link href={outerUrl}>
						{' '}
						<StyledTitle dangerouslySetInnerHTML={{ __html: title }} />
					</Link>
					<StyledSubtitle inTrendingArticles={inTrendingArticles}>
						{inTrendingArticles
							? subtitle
							: `By ${authorString} | ${adviceText} ${dayjs(displayModified).format('MMMM DD, YYYY')}`}
					</StyledSubtitle>
					{!!blurb && (
						<StyledBlurb dangerouslySetInnerHTML={{ __html: truncateString(blurb, MAX_BLURB_CHARS) }} />
					)}
					{author && inTrendingArticles && (
						<AuthorInfo author={author} sponsor={sponsor} isSponsored={isSponsored} />
					)}
				</ContentWrapper>
			</StyledArticle>
		</>
	)
}
